<template>
  <lateral-modal
    id="user-actions-modal"
    :title="$t('user_actions_modal_header')"
    containerClass="user-actions-lateral"
    @close="closeModal"
  >
    <template slot="extraAction">
      <div
        class="d-flex justify-content-center"
      >
        <button
          v-if="allowRemove"
          class="Fuell-Button red block"
          :disabled="isRemovingUser"
          :class="{ disabled: isRemovingUser }"
          @click="showConfirmModal"
        >
          {{ $t(cancelOrRemoveUserHeaderLabel) }}
        </button>
      </div>
    </template>
    <template slot="content">
      <template v-if="user">
        <div
          v-if="user"
          class="user-avatar"
        >
          <user-name-badge
            :userName="fullUserName"
            :userAvatarId="userAvatarId"
            :thumbSizeClass="'Thumb--72'"
            overrideMargins
          ></user-name-badge>
        </div>
        <div class="container-tabs">
          <div
            class="tab"
            :class="{'active': activeTab === 'INFORMATION', 'w-100' : !userHasCards}"
            @click="setActiveTab('INFORMATION')"
          >{{$t('user_actions_modal_information')}}</div>
          <div
            class="tab"
            :class="{'active': activeTab === 'SETTINGS'}"
            @click="setActiveTab('SETTINGS')"
            v-if="userHasCards"
          >{{$t('user_actions_modal_settings')}}</div>
        </div>
        <template>
          <template v-if="!loading">
            <div
              v-if="activeTab === 'INFORMATION'"
              class="row"
            >
              <form
                id="user-form"
                class="w-100"
                @submit.prevent="submit"
              >
                <div class="col-12">
                  <div class="input--block input--block--margin input--block--padded">
                    <label
                      for=""
                      class="form--label u-required"
                    >
                      {{ $t('NAME').toUpperCase() }}</label>
                    <div
                      class="Form-field"
                      :class="{'Form--field-error' : $v.userData.firstName.$error}"
                      v-focus-input
                    >
                      <input
                        class="Input-text"
                        v-model="userData.firstName"
                        :disabled="!canEditField"
                        @blur="$v.userData.firstName.$touch()"
                      />
                    </div>
                  </div>
                </div>
                <div class="col-12">
                  <div class="input--block input--block--margin input--block--padded">
                    <label
                      for=""
                      class="form--label u-required"
                    >
                      {{ $t('LAST_NAME').toUpperCase() }}</label>
                    <div
                      class="Form-field"
                      :class="{'Form--field-error' : $v.userData.lastName.$error}"
                    >
                      <input
                        class="Input-text"
                        v-model="userData.lastName"
                        :disabled="!canEditField"
                        @blur="$v.userData.lastName.$touch()"
                      />
                    </div>
                  </div>
                </div>
                <div class="col-12">
                  <div class="input--block input--block--margin input--block--padded">
                    <label class="form--label u-required">
                      {{ $t("EMAIL").toUpperCase() }}
                    </label>
                    <div
                      class="Form-field"
                      :class="{ 'Form--field-error': $v.userData.email.$error }"
                    >
                      <input
                        type="text"
                        class="Input-text"
                        v-model="userData.email"
                        :disabled="isDisabled"
                        @blur="$v.userData.email.$touch()"
                      />
                    </div>
                  </div>
                </div>
                <div class="col-12">
                  <div class="input--block input--block--margin input--block--padded">
                    <label
                      for=""
                      class="form--label u-required"
                    >
                      {{ $t('PHONE_NUMBER').toUpperCase() }}</label>
                    <div class="Form-field">
                      <div class="phone-box">
                        <multi-select-input
                          :id="'multiselect-input-prefix'"
                          :value="userData.phonePrefix"
                          :customLabelField="'phonePrefix'"
                          :selectLabel="''"
                          :deselectLabel="''"
                          :selectedLabel="''"
                          @inputSelected="setPhonePrefix($event)"
                          @close="touchPhoneFields"
                          :disabled="isDisabled"
                          :placeholder="$t('PHONE_PREFIX')"
                          :options="phonePrefixList"
                        ></multi-select-input>
                        <input
                          class="Input-text"
                          @input="touchPhoneFields"
                          :placeholder="$t('PHONE_NUMBER')"
                          :disabled="isDisabled"
                          type="text"
                          v-model="userData.phone"
                        >
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-12">
                  <div class="input--block input--block--margin input--block--padded">
                    <label
                      for=""
                      class="form--label"
                    >
                      {{ $t('IDENTIFIER').toUpperCase() }}</label>
                    <div class="Form-field">
                      <input
                        class="Input-text"
                        v-model="userData.identifier"
                        :disabled="isUser"
                      />
                    </div>
                  </div>
                </div>
                <div class="col-12">
                  <div class="input--block input--block--margin-top input--block--padded">
                    <label
                      for=""
                      class="form--label"
                    >
                      {{ $t('EXPENSE_CENTER').toUpperCase() }}</label>
                    <div class="Form-field">
                      <input
                        class="Input-text"
                        v-model="userData.expenseCenter"
                        :disabled="isUser"
                      />
                    </div>
                  </div>
                </div>
                <div class="col-12 d-flex">
                  <button
                    class="Fuell-Button-Link blue"
                    type="submit"
                    @click="submit"
                    :disabled="!canBeSubmitted"
                    form="user-form"
                    v-t.preserve="{ path: 'ACTION_SAVE_CHANGES' }"
                  ></button>
                </div>
              </form>
              <template v-if="isSuperAdmin">
                <div class="col-12 d-flex justify-content-center mt-4">
                  <h2>{{$t('user_actions_modal_user_actions')}}</h2>
                </div>
                <template v-if="isSuperAdmin">
                  <div class="col-12">
                    <div class="input--block input--block--margin input--block--padded">
                      <label
                        for=""
                        class="form--label"
                      >
                        {{ $t('ROLES').toUpperCase() }}</label>
                      <div class="Form-field">
                        <span class="Fuell-Text d-flex w-100">
                          {{$t('USER')}} {{userRoleLabel}}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="col-12 d-flex">
                    <button
                      type="button"
                      class="Fuell-Button-List impersonate-user"
                      @click="impersonateUser"
                    >
                      {{$t('users_customers_admin_impersonate_user')}}
                    </button>
                  </div>
                  <div
                    v-if="userIsRegular"
                    class="col-12 d-flex"
                  >
                    <button
                      type="button"
                      class="Fuell-Button-List hover-warning make-admin"
                      @click="showConfirmMakeAdmin"
                    >
                      {{$t('user_profile_upgrade_to_admin')}}
                    </button>
                  </div>
                  <!-- CAMBIAR CLAVE -->
                  <div class="col-12 d-flex">
                    <button
                      class="Fuell-Button-List hover-warning change-password"
                      @click.prevent="showChangePasswordModal"
                    >
                      {{ $t('user_profile_change_passcode') }}
                    </button>
                  </div>
                  <!-- FIN CAMBIAR CLAVE -->
                  <div
                    v-if="userInvitationPending"
                    class="col-12 d-flex"
                  >
                    <button
                      class="Fuell-Button-List resend-invitation"
                      :class="{ disabled: isReinvitingUser }"
                      :disabled="isReinvitingUser"
                      @click.prevent="reinviteUser"
                    >
                      {{ isReinvitingUser ? $t('ACTION_REINVITING') : $t('ACTION_REINVITE') }}
                    </button>
                  </div>
                </template>
                <div
                  v-if="(isAdmin || isUser) && !userIsAdminOwner && isSuperAdmin"
                  class="col-12 d-flex"
                >
                  <button
                    class="Fuell-Button-List red hover-red delete-user"
                    :disabled="isRemovingUser"
                    :class="{ disabled: isRemovingUser }"
                    @click.prevent="showConfirmModal"
                  >
                    {{ isRemovingUser ? $t('media_viewer_deleting_resource') : $t(cancelOrRemoveUserHeaderLabel) }}
                  </button>
                </div>
                <div
                  v-else-if="!userIsAdminOwner && userInvitationPending && isSuperAdmin"
                  class="col-12 d-flex"
                >
                  <button
                    class="Fuell-Button-List red hover-red cancel-invitation"
                    :disabled="isRemovingUser"
                    :class="{ disabled: isRemovingUser }"
                    @click.prevent="showConfirmModal"
                  >
                    {{ $t('user_action_cancel_invitation') }}
                  </button>
                </div>
                <template v-if="showCardOptions">
                  <!-- OPERACIONES CON TARJETAS -->
                  <div class="col-12 d-flex justify-content-center mt-4">
                    <h2>{{$t('user_actions_modal_card_actions')}}</h2>
                  </div>
                  <div
                    class="col-12"
                    v-if="userHasCards && userHasPhysicalCards"
                  >
                    <template v-if="!isFetchingCards">
                      <div
                        v-if="userCards.length > 0"
                        class="w-100 d-flex mt-2"
                      >
                        <button
                          v-for="card in userCards"
                          :key="card.id"
                          :class="{'red hover-red': card.externalId, 'disabled': !card.externalId}"
                          :disabled="checkCardDisabled(card) && !isCancellingCard || !card.externalId"
                          type="button"
                          class="Fuell-Button-List remove-card"
                          @click="showConfirmCancelCard(card)"
                        >
                          <i18n
                            tag="span"
                            path="customer_user_actions_cancel_card"
                          >
                            <template v-slot:numMP>
                              <template v-if="card.externalId">
                                {{card.externalId}}
                              </template>
                              <template v-else>
                                {{$t('customer_user_card_not_emmited')}}
                              </template>
                            </template>
                          </i18n>
                        </button>
                      </div>
                      <div
                        class="w-100 d-flex"
                        v-else
                      >
                        <button
                          v-if="userHasCards && !userInvitationPending"
                          type="button"
                          class="Fuell-Button-List request-card"
                          @click="showRequestCardModal = true"
                        >
                          {{$t('customers_user_actions_reequest_card')}}
                        </button>
                      </div>
                    </template>
                    <template v-else>
                      <div class="w-100 d-flex justify-content-center">
                        <spinner />
                      </div>
                    </template>
                  </div>
                  <div
                    class="col-12"
                    v-else-if="userCanRequestCards"
                  >
                    <div class="w-100 d-flex">
                      <button
                        @click="showRequestCardModal = true"
                        class="Fuell-Button-List request-card"
                      >
                        {{$t('customer_user_request_physical_card')}}
                      </button>
                    </div>
                  </div>
                  <div class="col-12" v-else-if="userWithoutCards">
                    <div class="w-100 d-flex">
                      <button
                        @click="showConvertToCardsUserModalFlag = true"
                        class="Fuell-Button-List request-card"
                      >
                        {{$t('customer_user_convert_to_user_with_cards')}}
                      </button>
                    </div>
                  </div>
                </template>
              </template>
            </div>
            <!-- FIN TAB INFORMAICÓN-->
            <div
              v-else
              class="col-12 container-settings-user"
            >
              <toggle-switch
                label="user_actions_modal_online_payments"
                :status.sync="user.onlinePaymentActivated"
                :labelClass="'Label Label--text'"
                @change="toggleOnlinePayments()"
              ></toggle-switch>
              <p class="input--toggle-block-description">{{ $t('user_actions_modal_online_payments_description') }}</p>
            </div>
          </template>
          <template v-else>
            <div class="row">
              <div class="w-100 d-flex justify-content-center">
                <spinner />
              </div>
            </div>
          </template>
        </template>

      </template>
    </template>
    <template slot="modals">
      <confirm-modal
        v-if="showChangePasswordModalFlag"
        :title="$t('user_profile_change_passcode')"
        :message="$t('user_profile_change_password_message')"
        :acceptText="$t('user_profile_change_passcode')"
        :open.sync="showChangePasswordModalFlag"
        :disabledConfirm="!newPasscode || isChangingPasscodeFlag"
        :acceptBtnClass="'blue'"
        closeButton
        hideDismissButton
        @accept="showConfirmChangePasscode"
      >
        <template v-slot:additional>
          <div class="input--block mt-3">
            <label class="form--label">
              {{ $t("user_profile_change_passcode").toUpperCase() }}
            </label>
            <div class="Form-field">
              <input
                type="password"
                class="Input-text"
                maxlength="48"
                :placeholder="$t('PASSCODE')"
                autocomplete="new-password"
                v-model.trim="newPasscode"
              />
            </div>
          </div>
        </template>
      </confirm-modal>
      <confirm-modal
        v-if="showConfirmModalFlag"
        :title="cancelOrRemoveUserHeaderLabel"
        ignoreDefaultMessage
        :message="''"
        :acceptText="cancelOrRemoveUserHeaderLabel"
        :dismissText="'ACTION_CANCEL'"
        :open.sync="showConfirmModalFlag"
        @accept="cancelInvitation"
        @dismissDelete="dismissCancel"
      >
        <template v-slot:additional>
          <i18n
            tag="span"
            class="Fuell-Text"
            :path="'users_actions_confirm_modal_message'"
          >
            <template v-slot:action>
              <span>{{ $t(cancelOrRemoveUserActionLabel) }}</span>
            </template>
            <template v-slot:user>
              <span class="text-primary">{{ fullUserName }}</span>
            </template>
            <template v-slot:email>
              <span class="text-primary"> {{ user.email }}</span>
            </template>
          </i18n>
        </template>
      </confirm-modal>
      <confirm-modal
        v-if="showConfirmChangePasscodeFlag"
        :open.sync="showConfirmChangePasscodeFlag"
        :message="''"
        :title="'user_profile_change_passcode'"
        :acceptText="'user_profile_change_passcode'"
        @accept="updatePascode"
        @dismissDelete="dismissCancel"
        :disabledConfirm="otp.length === 0 || isCallingApi"
        :acceptBtnClass="otp.length === 0 ? 'red disabled' : 'red'"
        ignoreDefaultMessage
      >
        <template v-slot:additional>
          <p class="Fuell-Text">
            {{$t('customer_actions_request_otp')}}
          </p>
          <div class="row d-flex justify-content-between align-items-center">
            <div class="col-9">
              <div class="input--block input--block--margin input--block--padded">
                <label class="form--label"> {{$t('OTP').toUpperCase()}} </label>
                <div class="Form-field">
                  <input
                    type="password"
                    class="Input-text"
                    placeholder="OTP"
                    autocomplete="new-password"
                    v-model.trim="otp"
                  />
                </div>
              </div>
            </div>
            <div class="col-3 mt-4">
              <button
                type="button"
                class="Fuell-Button blue block"
                @click="generateOTP"
                :disabled="isCallingApi"
              >
                {{$t('customer_actions_request_otp_action')}}
              </button>
            </div>
            <small class="col-12 ml-1">{{$t('customer_actions_request_otp_warning')}}</small>
            <div
              class="
              col-12
              d-flex
              flex-column
              justify-content-center
              align-items-center
              w-100
            "
              v-if="isCallingApi"
            >
              <spinner />
              <span
                class="Fuell-Text"
                style="font-size: 0.9em"
              >{{$t('customer_actions_request_otp_in_progress')}}</span>
            </div>
          </div>
        </template>
      </confirm-modal>
      <confirm-modal
        v-if="showConfirmCancelCardFlag"
        :open.sync="showConfirmCancelCardFlag"
        :message="''"
        ignoreDefaultMessage
        :acceptText="'customers_users_actions_cancel_card_confirm_action'"
        :dismissText="'ACTION_CANCEL'"
        @accept="cancelCard"
        @dismissDelete="dismissCancel"
      >
        <template v-slot:additional>
          <i18n
            tag="p"
            path="customers_users_actions_cancel_card_confirm"
            class="Fuell-Text"
          >
            <template v-slot:userName><span class="text-primary">{{fullUserName}}</span></template>
            <template v-slot:numMP> <span class="text-primary"> {{selectedCard.externalId}}</span></template>
          </i18n>
        </template>
      </confirm-modal>
      <super-admin-create-card-modal
        :user="user"
        :open.sync="showRequestCardModal"
        @updateCards="updateUserInfo"
        v-if="showRequestCardModal"
      />
      <confirm-modal
        v-if="showConfirmMakeAdminModalFlag"
        :open.sync="showConfirmMakeAdminModalFlag"
        :title="'user_profile_upgrade_to_admin'"
        :acceptText="'user_profile_upgrade_to_admin'"
        :dismissText="'ACTION_CANCEL'"
        @accept="confirmMakeAdmin"
      >
      </confirm-modal>
      <super-admin-convert-to-cards-user-modal
      v-if="showConvertToCardsUserModalFlag"
      :open.sync="showConvertToCardsUserModalFlag"
      :user="user"
      @updateCards="updateUserInfo"
      />
    </template>
  </lateral-modal>
</template>

<script>
import { USER_CARDS_TYPE } from '@/constants/userCardsType'
import { OFFICE_CARDS_TYPE } from '@/constants/officeCardsType'
import usersApi from '../../_api'
import cardsApi from '@/modules/cards/_api'
import _modalHandlerMixin from '@/components/Commons/_mixins/_modalHandlerMixin'
import ToggleSwitch from '@/components/Commons/Forms/ToggleSwitch'
import UserNameBadge from '@/components/Commons/UserNameBadge'
import Spinner from '@/components/Commons/Spinner'
import MultiSelectInput from '@/components/Commons/Forms/MultiSelectInput'
import LateralModal from '@/components/Commons/Modals/LateralModal'
import _userRoleHandlerMixin from '../../_mixins/_userRoleHandlerMixin'
const SuperAdminConvertToCardsUserModal = () => import('./SuperAdminConvertToCardsUserModal.vue')
const ConfirmModal = () => import('@/components/Commons/ConfirmModal')
const SuperAdminCreateCardModal = () =>
  import('@/modules/users/_components/_shared/SuperAdminCreateCardModal')
const { validationMixin } = require('vuelidate')
const { required, email } = require('vuelidate/lib/validators')
export default {
  name: 'UserActionsModal',
  mixins: [_modalHandlerMixin, _userRoleHandlerMixin, validationMixin],
  components: {
    UserNameBadge,
    MultiSelectInput,
    ConfirmModal,
    SuperAdminCreateCardModal,
    SuperAdminConvertToCardsUserModal,
    Spinner,
    LateralModal,
    ToggleSwitch
  },
  props: {
    userId: [Number, String]
  },

  data () {
        return {
      showConfirmModalFlag: false,
      showChangePasswordModalFlag: false,
      modalIsOpen: true,
      userData: {
        email: '',
        phone: '',
        phonePrefix: '',
        firstName: '',
        lastName: '',
        identifier: '',
        expenseCenter: ''
      },
      original: {},
      newPasscode: '',
      otp: '',
      otpSuccessFlag: false,
      isCallingApi: false,
      showConfirmChangePasscodeFlag: false,
      showConfirmMakeAdminModalFlag: false,
      isChangingPasscodeFlag: false,
      isRemovingUser: false,
      isReinvitingUser: false,
      isFetchingCards: false,
      userCards: [],
      showConfirmCancelCardFlag: false,
      selectedCard: null,
      isCancellingCard: false,
      showRequestCardModal: false,
      loading: false,
      activeTab: 'INFORMATION',
      showConvertToCardsUserModalFlag: false
    }
  },
  computed: {
    phonePrefixList () {
      return this.$store.getters['ConfigStore/publicConfig'].countries || []
    },
    isSuperAdmin () {
      return this.$store.getters['LoginStore/isSuperAdmin']
    },
    isUser () {
      return this.$store.getters['LoginStore/isUser']
    },
    isAdmin () {
      return this.$store.getters['LoginStore/isAdmin']
    },
    isDisabled () {
      if (this.isSuperAdmin) return false
      return this.userIsAdminOwner || this.isUser
    },
    canEditField () {
      if (this.isSuperAdmin) return true
      if (this.isAdmin || this.isUser) {
        return !this.userHasCards
      } else {
        return false
      }
    },
    user () {
      return this.$store.getters['UsersStore/user'] || {}
    },
    adminOwnerRoles () {
      return this.$store.getters['LoginStore/adminOwnerRoles'] || []
    },
    adminRoles () {
      return this.$store.getters['LoginStore/adminRoles'] || []
    },
    userIsAnyAdmin () {
      return this.user?.roles?.some(el => this.adminRoles.includes(el))
    },
    userIsAdminOwner () {
      return this.user?.roles?.some(el => this.adminOwnerRoles.includes(el))
    },
    userIsSuperAdmin () {
      return this.user?.roles?.includes('ROLE_SUPER_ADMIN')
    },
    userIsRegular () {
      return this.user?.roles?.includes('ROLE_REGULAR')
    },
    userIsBookKeeper () {
      return this.user?.roles?.includes('ROLE_BOOKKEEPER')
    },
    fullUserName () {
      return this.user && this.user.firstName && this.user.lastName
        ? `${this.user.firstName} ${this.user.lastName}`
        : ''
    },
    fullUserPhone () {
      return this.user &&
        this.user.phonePrefix !== null &&
        this.user.phone !== null
        ? `${this.user.phonePrefix.phonePrefix} - ${this.user.phone}`
        : ''
    },
    userStatusLabel () {
      return this.user && this.user.status && this.user.status.statusType
        ? `user_status__${this.user.status.statusType.code}`
        : ''
    },
    userAvatarId () {
      return this.user &&
        this.user.profilePicture &&
        this.user.profilePicture.id
        ? this.user.profilePicture.id
        : false
    },

    userInvitationPending () {
      return (
        (this.user &&
          this.user.status &&
          this.user.status.statusType.code === 'INVITE_BY_ADMIN') || this.user?.status?.statusType?.code === 'INVITE_BY_SUPER_ADMIN'
      )
    },
    cancelOrRemoveUserHeaderLabel () {
      return this.userInvitationPending
        ? 'users_confirm_cancel_invitation_header'
        : 'users_confirm_remove_user_header'
    },
    cancelOrRemoveUserActionLabel () {
      return this.userInvitationPending
        ? 'user_action_cancel_invitation_warning'
        : 'user_action_remove_warning'
    },
    hasLocalChanges () {
      return JSON.stringify(this.original) !== JSON.stringify(this.userData)
    },
    canBeSubmitted () {
      return !this.$v.$invalid && this.hasLocalChanges
    },
    loggedUserId () {
      return this.$store.getters['LoginStore/user'].id || false
    },
    userCardsType () {
      return this.user.userCardsType
    },
    allowRemove () {
      if (this.isUser) {
        return !this.userIsAnyAdmin
      }
      if (this.isAdmin || this.isSuperAdmin) {
        return !this.userIsAdminOwner
      }
      return false
    },
    userOfficeId () {
      return this.user?.office?.id ?? 0
    },
    isCallingCompany () {
      return this.$store.getters['CompanyStore/isCallingCompany']
    },
    currentUserCompany () {
      return this.$store.getters['CompanyStore/currentCompany']
    },
    currentUserCompanyOfficeCardsType () {
      return this.$store.getters['CompanyStore/currentCompany']?.officeCardsType ?? false
    },
    currentUserCompanyCanManageCards () {
      return [OFFICE_CARDS_TYPE.OFFICE_CARDS_ALWAYS, OFFICE_CARDS_TYPE.OFFICE_CARDS_OPTIONAL].includes(this.currentUserCompanyOfficeCardsType)
    },
    userHasCards () {
      return this.userCardsType === USER_CARDS_TYPE.USER_CARDS_ALWAYS && !this.userIsCommercial && !this.userIsSuperBook
    },
    userTypeOfCardRegisteredOnIssuer () {
      return this.user?.typeOfCardWhenRegisteredOnIssuer
    },
    userHasPhysicalCards () {
      return this.user?.typeOfCardWhenRegisteredOnIssuer === 'PHYSICAL'
    },
    userWithoutCards () {
      return this.userCardsType === USER_CARDS_TYPE.USER_CARDS_WITHOUT
    },
    userCanManageCards () {
      return this.userIsRegular || this.userIsAnyAdmin
    },
    userCanRequestCards () {
      return this.userCanManageCards && (this.userCards.length === 0 || !this.userHasPhysicalCards) && !this.userWithoutCards
    },
    showCardOptions () {
      if (this.currentUserCompanyCanManageCards && this.userCanManageCards && this.isSuperAdmin && !this.userInvitationPending) {
        return true
      } else {
        return false
      }
    }

  },
  methods: {
    showChangePasswordModal () {
      this.showChangePasswordModalFlag = true
    },
    async toggleOnlinePayments () {
      let updateUserData = {
        id: this.userId,
        onlinePaymentActivated: this.user.onlinePaymentActivated
      }
      await this.$store.dispatch('UsersStore/updateUserOnlinePayment', updateUserData)
    },
    setActiveTab (tab) {
      this.activeTab = tab
    },
    async impersonateUser () {
      await this.$store.dispatch('LoginStore/impersonateUser', this.userId)
      this.$forceUpdate()
    },
    touchPhoneFields () {
      this.$v.userData.phone.$touch()
      this.$v.userData.phonePrefix.$touch()
    },
    setPhonePrefix (event) {
      this.userData.phonePrefix = event
    },
    showConfirmModal () {
      this.showConfirmModalFlag = true
    },
    showConfirmChangePasscode () {
      this.showConfirmChangePasscodeFlag = true
    },
    showConfirmCancelCard (card) {
      this.selectedCard = card
      this.showConfirmCancelCardFlag = true
    },
    showConfirmMakeAdmin () {
      this.showConfirmMakeAdminModalFlag = true
    },
    async confirmMakeAdmin () {
      try {
        if (this.isCallingApi) return
        this.isCallingApi = true
        await usersApi.makeAdmin(this.userId)
        this.$store.dispatch('SnackBars/addSuccess', this.$t('superadmin_make_admin_success'), { root: true })
        this.$emit('refreshUsers')
      } catch (error) {
        this.$store.dispatch('SnackBars/addErrorHandler', { error: error, errorMessage: 'superadmin_make_admin_error' }, { root: true })
      } finally {
        this.isCallingApi = false
      }
    },
    async generateOTP () {
      try {
        if (this.isCallingApi) return
        this.isCallingApi = true
        await usersApi.putUserGenerateOneTimePassword(this.loggedUserId)
        this.$store.dispatch(
          'SnackBars/addSuccess',
          this.$t('superadmin_generate_otp_success'),
          { root: true }
        )
      } catch (error) {
        this.$store.dispatch(
          'SnackBar/addErrorHandler',
          { error: error, errorMessage: 'superadmin_generate_otp_error' },
          { root: true }
        )
      } finally {
        this.isCallingApi = false
      }
    },
    async cancelInvitation () {
      this.showConfirmModalFlag = false
      this.isRemovingUser = true
      await this.$store.dispatch('UsersStore/removeUser', {
        userId: this.userId,
        user: this.user
      })
      this.isRemovingUser = false
      this.modalIsOpen = false
      this.closeModal()
    },
    async reinviteUser () {
      this.isReinvitingUser = true
      await this.$store.dispatch('UsersStore/reinviteUser', {
        userId: this.userId
      })
      this.isReinvitingUser = false
      this.closeModal()
    },
    async updatePascode () {
      if (this.isChangingPasscodeFlag) return
      try {
        this.isChangingPasscodeFlag = true
        await usersApi.putForceUpdatePasscode(this.userId, {
          passcode: this.newPasscode,
          otp: this.otp
        })
        this.$store.dispatch(
          'SnackBars/addSuccess',
          this.$t('user_update_passcode_success')
        )
        this.closeModal()
      } catch (error) {
        this.$store.dispatch('SnackBars/addErrorHandler', {
          error: error,
          errorMessage: 'user_update_passcode_error'
        })
      } finally {
        this.isChangingPasscodeFlag = false
        this.newPasscode = ''
        this.otp = ''
      }
    },
    dismissCancel () { },
    _initialize () {
      this.userData.email = this.user.email || ''
      this.userData.phone = this.user.phone || ''
      this.userData.phonePrefix = this.user?.phonePrefix?.id ? this.user.phonePrefix?.id : ''
      this.userData.firstName = this.user.firstName || ''
      this.userData.lastName = this.user.lastName || ''
      this.userData.identifier = this.user.identifier || ''
      this.userData.expenseCenter = this.user.expenseCenter || ''
      this.original = { ...this.userData }
    },
    async submit () {
      this.loading = true
      let updateUserData = {
        id: this.userId,
        firstName: this.userData.firstName,
        lastName: this.userData.lastName,
        email: this.userData.email,
        phone: this.userData.phone,
        phonePrefix: { id: this.userData.phonePrefix },
        expenseCenter: this.userData.expenseCenter,
        identifier: this.userData.identifier
      }
      await this.$store.dispatch('UsersStore/updateUserList', updateUserData).finally(() => {
        this.loading = false
        this.closeModal()
      })
    },
    async fetchUserCards () {
      if (this.isFetchingCards) return
      try {
        this.isFetchingCards = true
        const cardsFilters = {
          'user.id': this.userId,
          'type.code': 'MEMBER_PHYSICAL'
        }
        const { data } = await cardsApi.getCards(1, cardsFilters)
        this.userCards = [...data]
      } catch (error) {
        this.$store.dispatch(
          'SnackBars/addErrorHandler',
          { error: error, errorMessage: 'cards_action_fetch_cards_error' },
          { root: true }
        )
        console.error(error)
      } finally {
        this.isFetchingCards = false
      }
    },
    checkCardDisabled (card) {
      if (!card) return true
      return card?.status?.code !== 'ACTIVE' && card?.status?.code !== 'FROZEN' && card?.status?.code !== 'FROZEN_BY_ADMIN'
    },
    async cancelCard () {
      if (!this.selectedCard) return
      if (this.isCancellingCard) return
      try {
        this.isCancellingCard = true
        await cardsApi.putCancelCard(this.selectedCard.id, {
          passcode: ''
        })
        this.userCards = [
          ...this.userCards.filter((el) => el.id !== this.selectedCard.id)
        ]
        this.$store.dispatch(
          'SnackBars/addSuccess',
          this.$i18n.t('customers_users_actions_cancel_card_success'),
          { root: true }
        )
        await this.fetchUser()
      } catch (error) {
        this.$store.dispatch(
          'SnackBars/addErrorHandler',
          { error: error, errorMessage: 'customers_users_actions_cancel_card_error' },
          { root: true }
        )
      } finally {
        this.isCancellingCard = false
        this.showConfirmCancelCardFlag = false
        this.selectedCard = null
      }
    },
    async fetchUser () {
      await this.$store.dispatch('UsersStore/fetchUser', this.userId)
    },
    async updateUserInfo () {
      await this.fetchUser()
      await this.fetchUserCards()
    },
    async fetchUserOffice () {
      await this.$store.dispatch('CompanyStore/fetchCompany', this.userOfficeId)
    }
  },
  validations: {
    userData: {
      email: { required, email },
      phonePrefix: { required },
      phone: { required },
      firstName: { required },
      lastName: { required },
      identifier: {},
      expenseCenter: {}
    }
  },
  async created () {
    this.loading = true
    await this.fetchUserCards()
    await this.fetchUser()
    if (this.isSuperAdmin) {
      await this.fetchUserOffice()
    }
    this._initialize()
    this.loading = false
  }
}
</script>
<style scoped src="@/assets/styles/vue-multiselect-phone.css"></style>
<style scoped lang="scss">
h2 {
  margin: 0;
  text-align: center;
  font-size: 15px;
  font-weight: 500;
  line-height: 24px;
}
.input--block--margin-top {
  margin-top: 10px;
}
.phone-box >>> .multiselect__placeholder {
  margin-top: -3px;
}
.Fuell-Button[disabled] {
  color: var(--text-disabled);
  -webkit-text-fill-color: var(--text-disabled);
  opacity: 1;
}
.user-avatar {
  display: flex;
  align-items: center;
  margin-top: 20px;
  margin-bottom: 17px;
  justify-content: center;
}
.container-tabs {
  display: flex;
  height: 40px;
  padding: 2px;
  background: var(--fill-background);
  cursor: pointer;
  .tab {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 50%;
    font-weight: 400;
    font-size: 15px;
    line-height: 24px;
    text-align: center;
    color: var(--text-placeholder);
    &.active {
      background: var(--white);
      border: 1px solid var(--fill-border);
      box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.08);
      border-radius: 2px;
      font-weight: 600;
      color: var(--text-heading);
    }
  }
}
.container-settings-user {
  margin-top: 24px;
}
</style>
<style lang="scss">
#user-actions-modal {
  .Fuell-Toggle-switch {
    order: 1;
    margin-left: auto;
    margin-right: 0 !important;
  }
  .Label--text {
    font-weight: 500;
    font-size: 15px;
    line-height: 24px;
    color: var(--text-heading);
  }
  .input--toggle-block {
    .d-flex {
      justify-content: space-between;
    }
    &-description {
      font-weight: 400;
      font-size: 15px;
      line-height: 24px;
      color: var(--text-body);
    }
  }
}
</style>
<style>
.ModalBox-container.user-actions-lateral {
  padding-left: 8px !important;
  padding-right: 8px !important;
}
</style>
